import {
  useDeleteLayoutResultMutation,
  useUpdateLayoutOptimizationResultMutation,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ResultFileType } from '@consigli/types';
import { useState, useEffect } from 'react';

import { ModalAlert, Modal, Select, Field } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { useResultsContext } from './results-context';

export const DeleteResultModal = () => {
  const { deleteItem, setDeleteItem, projectId, layoutId } = useResultsContext();
  const [deleteResult, { isLoading }] = useDeleteLayoutResultMutation();

  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      await deleteResult({
        projectId,
        layoutId,
        resultId: deleteItem.id,
      }).unwrap();
      successToast({ message: 'Result deleted successfully' });
      setDeleteItem(null);
    } catch (error) {
      console.error('Failed to delete result:', error);
      errorToast({ message: 'Failed to delete result' });
    }
  };

  return (
    <ModalAlert
      isOpen={!!deleteItem}
      onOpenChange={(open) => !open && setDeleteItem(null)}
      title="Delete Result"
      description={`Are you sure you want to delete "${deleteItem?.file.originalFilename}"? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      onConfirm={handleDelete}
      isLoading={isLoading}
      destructive
    />
  );
};

const fileTypeOptions = Object.values(ResultFileType).map((type) => ({
  label: type,
  value: type,
}));

export const EditResultTypeModal = () => {
  const { editItem, setEditItem, projectId, layoutId } = useResultsContext();
  const [selectedType, setSelectedType] = useState<ResultFileType | null>(null);
  const [updateResult, { isLoading }] = useUpdateLayoutOptimizationResultMutation();

  // Initialize selectedType when editItem changes
  useEffect(() => {
    if (editItem) {
      setSelectedType(editItem.type);
    }
  }, [editItem]);

  const handleSave = async () => {
    if (!editItem || !selectedType) return;

    try {
      await updateResult({
        projectId,
        layoutId,
        resultId: editItem.id,
        data: {
          type: selectedType,
        },
      }).unwrap();
      successToast({ message: 'Result type updated successfully' });
      setEditItem(null);
      setSelectedType(null);
    } catch (error) {
      console.error('Failed to update result type:', error);
      errorToast({ message: 'Failed to update result type' });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setEditItem(null);
      setSelectedType(null);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={() => setEditItem(null)} disabled={isLoading}>
        Cancel
      </Button>
      <Button onClick={handleSave} disabled={isLoading || !selectedType}>
        {isLoading ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={!!editItem}
      onOpenChange={handleOpenChange}
      title="Edit Result Type"
      description={`Change the file type for "${editItem?.file.originalFilename}"`}
      footer={footer}
    >
      <Field label="File Type" htmlFor="file-type-select">
        <Select
          options={fileTypeOptions}
          placeholder="Select file type"
          value={fileTypeOptions.find((opt) => opt.value === selectedType)}
          onValueChange={(option) => setSelectedType(option.value as ResultFileType)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          className="w-full"
        />
      </Field>
    </Modal>
  );
};
